import { useState, useEffect, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { useToast } from "../components/ToastProvider";
import { useLocationStore } from "../features/locationStore";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_SERVICE_LOCATIONS } from "../features/location/locationQueries";
import { CREATE_CUSTOMER_LOCATION } from "../features/location/locationMutations";

interface LocationForm {
  label: string;
  floorNumber: string;
  doorNumber: string;
  buildingNumber: string;
  streetNumber: string;
  zoneNumber: string;
  area: string;
}

interface CustomerLocation {
  id: string;
  location: string;
  is_active: boolean;
}

const emptyForm: LocationForm = {
  label: "",
  floorNumber: "",
  doorNumber: "",
  buildingNumber: "",
  streetNumber: "",
  zoneNumber: "",
  area: "",
};

export default function AddEditLocationScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { addLocation, updateLocation, deleteLocation, getLocation } =
    useLocationStore();

  const isEdit = Boolean(id);
  const [formData, setFormData] = useState<LocationForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  interface ServiceLocationsResponse {
    serviceLocations: CustomerLocation[];
  }

  const {
    data: locationData,
    loading: locationsLoading,
    error: locationsError,
  } = useQuery<ServiceLocationsResponse>(GET_SERVICE_LOCATIONS, {
    fetchPolicy: "network-only",
  });

  const [createCustomerLocation, { loading: savingLocation }] = useMutation(
    CREATE_CUSTOMER_LOCATION,
    {
      refetchQueries: [{ query: GET_SERVICE_LOCATIONS }],
      awaitRefetchQueries: true,
    },
  );

  useEffect(() => {
    if (locationsError) {
      showToast("Failed to load areas", "error");
    }
  }, [locationsError, showToast]);

  useEffect(() => {
    if (isEdit && id) {
      const existing = getLocation(id);
      if (existing) {
        setFormData({
          label: existing.label,
          floorNumber: existing.floorNumber ?? "",
          doorNumber: existing.doorNumber ?? "",
          buildingNumber: existing.buildingNumber,
          streetNumber: existing.streetNumber,
          zoneNumber: existing.zoneNumber,
          area: existing.area,
        });
      }
    }
  }, [id, isEdit, getLocation]);

  const clearError = (field: string) => {
    setFieldErrors((prev) => {
      const u = { ...prev };
      delete u[field];
      return u;
    });
  };

  const handleChange = (field: keyof LocationForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.label.trim()) errors.label = "Required";
    if (!formData.floorNumber.trim()) errors.floorNumber = "Required";
    if (!formData.doorNumber.trim()) errors.doorNumber = "Required";
    if (!formData.buildingNumber.trim()) errors.buildingNumber = "Required";
    if (!formData.streetNumber.trim()) errors.streetNumber = "Required";
    if (!formData.zoneNumber.trim()) errors.zoneNumber = "Required";
    if (!formData.area) errors.area = "Required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const formattedLocation = `${formData.label} - Floor ${formData.floorNumber}, Door ${formData.doorNumber}, Building ${formData.buildingNumber}, Street ${formData.streetNumber}, Zone ${formData.zoneNumber}, ${formData.area}`;

    try {
      if (isEdit && id) {
        updateLocation(id, formData);

        showToast("Location updated successfully", "success");
      } else {
        await createCustomerLocation({
          variables: {
            location: formattedLocation,
          },
        });

        showToast("Location saved successfully", "success");
      }

      navigate(-1);
    } catch (error) {
      console.error("Create location error:", error);

      showToast("Failed to save location", "error");
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteLocation(id);
      showToast("Location deleted", "success");
      navigate(-1);
    }
  };

  const inputClass = (field: string) =>
    `w-full bg-gray-50 rounded-2xl px-4 py-3 text-gray-700 text-sm outline-none border transition-colors ${
      fieldErrors[field]
        ? "border-red-300 bg-red-50/50"
        : "border-gray-100 focus:border-[#44B6CE]"
    }`;

  return (
    <div className="bg-gray-50 min-h-screen pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-4 shadow-sm rounded-b-[32px] mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 border border-gray-100 rounded-xl flex items-center justify-center bg-white shadow-sm"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-lg font-bold font-poppins text-gray-900">
                {isEdit ? "Edit Location" : "Add New Location"}
              </h1>
              <p className="text-[11px] text-gray-400">
                Save address for quick checkout
              </p>
            </div>
          </div>
          {isEdit && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"
            >
              <Trash2 size={16} className="text-red-500" />
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-5 space-y-4">
        {/* Label Group */}
        <div className="bg-white rounded-[28px] p-5 shadow-sm border border-gray-100">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            Address Label
          </label>
          <input
            type="text"
            placeholder="e.g. Home, Office"
            value={formData.label}
            onChange={(e) => handleChange("label", e.target.value)}
            className={inputClass("label")}
          />
        </div>

        {/* Details Group */}
        <div className="bg-white rounded-[28px] p-5 shadow-sm border border-gray-100 space-y-4">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
            Location Details
          </label>

          {/* Floor & Door */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                Floor No.
              </label>
              <input
                type="text"
                placeholder="e.g. 3"
                value={formData.floorNumber}
                onChange={(e) => handleChange("floorNumber", e.target.value)}
                className={inputClass("floorNumber")}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                Door No.
              </label>
              <input
                type="text"
                placeholder="e.g. 12"
                value={formData.doorNumber}
                onChange={(e) => handleChange("doorNumber", e.target.value)}
                className={inputClass("doorNumber")}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                Building
              </label>
              <input
                type="text"
                placeholder="e.g. 24"
                value={formData.buildingNumber}
                onChange={(e) => handleChange("buildingNumber", e.target.value)}
                className={inputClass("buildingNumber")}
              />
            </div>
          </div>

          {/* Building, Street and Zone */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                Street
              </label>
              <input
                type="text"
                placeholder="e.g. 850"
                value={formData.streetNumber}
                onChange={(e) => handleChange("streetNumber", e.target.value)}
                className={inputClass("streetNumber")}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                Zone
              </label>
              <input
                type="text"
                placeholder="e.g. 55"
                value={formData.zoneNumber}
                onChange={(e) => handleChange("zoneNumber", e.target.value)}
                className={inputClass("zoneNumber")}
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
              Area
            </label>
            <select
              value={formData.area}
              onChange={(e) => handleChange("area", e.target.value)}
              disabled={locationsLoading}
              className={inputClass("area")}
            >
              <option value="" disabled>
                {locationsLoading ? "Loading areas..." : "Select Area"}
              </option>
              {locationData?.serviceLocations
                ?.filter((area: CustomerLocation) => area.is_active)
                .map((area: CustomerLocation) => (
                  <option key={area.id} value={area.location}>
                    {area.location}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={savingLocation}
          className={`w-full h-14 bg-[#44B6CE] text-white rounded-2xl flex items-center justify-center gap-2 font-bold shadow-lg shadow-cyan-100 active:scale-[0.98] transition-all ${
            savingLocation ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          <Save size={18} />
          {savingLocation
            ? "Saving..."
            : isEdit
              ? "Update Location"
              : "Save Location"}
        </button>
      </form>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] p-8 w-full max-w-sm shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Delete Address?
            </h3>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              This will remove this location from your saved list permanently.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 h-12 rounded-2xl border border-gray-200 text-gray-600 font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 h-12 rounded-2xl bg-red-500 text-white font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
